<template>
  <div class="document">
    <div class="header">
      <es-header>
        <template #header>
          <div class="header-title">
            Duplicate Search
          </div>
        </template>
      </es-header>
    </div>
    <es-loader
      v-if="searchSelected === 'queue'
        && (loadingQueue || loadingNewQueue)"
      style="margin: 6.5rem"
    />
    <es-exception
      v-else-if="searchSelected === 'queue'
        && (loadingErrorQueue || loadingErrorNewQueue)"
      exceptionMessage="There was an issue.
        Please try again or contact your administrator."
    />
    <div
      v-else
      class="no-border-top"
    >
      <div class="no-bottom-border">
        <es-form>
          <template #es-form>
            <div class="row removeUnderline">
              <div class="field is-horizontal">
                <div class="field-label is-normal">
                  <label class="label">
                    Search
                  </label>
                </div>
                <div class="field-body">
                  <div class="field">
                    <div class="control">
                      <div class="buttons has-addons">
                        <button
                          v-for="(type, index) in searchTypes"
                          :key="index"
                          class="button button--form"
                          :class="{ 'is-active': searchSelected === type.code }"
                          @click="searchSelected = type.code"
                        >
                          {{ type.description }}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div
              v-if="searchSelected === 'queue'"
              class="row removeUnderline"
            >
              <div class="field is-horizontal">
                <div class="field-label is-normal">
                  <label class="label">
                    Queue Name
                  </label>
                </div>
                <div class="field-body">
                  <div class="field">
                    <div class="control">
                      <input
                        v-if="!existingQueue"
                        type="text"
                        class="input"
                        v-model="queueName"
                        @keyup.prevent="validateQueueName"
                        :class="{ 'is-danger' : hasErrorQueueName }"
                        aria-label="Queue Name"
                      >
                      <div v-else>
                        <div v-if="allowQueueEdit">
                          <input
                            type="text"
                            class="input"
                            v-model="existingQueueName"
                            :class="{ 'is-danger' : hasErrorQueueName }"
                            aria-label="Queue Name"
                          >
                        </div>
                        <div
                          v-else
                          class="is-flex"
                        >
                          <input
                            aria-label="Queue Name"
                            class="input is-static"
                            disabled
                            readonly
                            tabindex="-1"
                            type="text"
                            v-model="existingQueueName"
                          >
                          <div class="queue-info">
                            Queue {{ existingQueue.status }} -
                            Created on {{ existingQueue.createdDate | formatDateMMDDYYYYHHMM }}
                            &nbsp;&nbsp;
                            Last Run Date: {{ existingQueue.lastRunDate | formatDateMMDDYYYYHHMM }}
                          </div>
                        </div>
                      </div>
                    </div>
                    <span
                      v-if ="hasErrorQueueName"
                      class="help is-danger"
                    >
                      {{ msgQueueName }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div class="row removeUnderline">
              <div class="field is-horizontal">
                <div class="field-label is-normal">
                  <label class="label">
                    Person Type
                  </label>
                </div>
                <div class="field-body">
                  <div class="field">
                    <div class="control">
                      <div class="buttons has-addons">
                        <button
                          v-for="(item, index) in sortedPersonTypesOptions"
                          :key="index"
                          :disabled="!allowQueueEdit
                            && searchSelected === 'queue'
                            && (existingQueueStatus === 'QUEUED'
                              || existingQueueStatus === 'RUNNING'
                              || existingQueueStatus === 'RERUN'
                              || existingQueueStatus === 'REFRESH'
                              || existingQueueStatus === 'COMPLETED')"
                          class="button button--form"
                          @click="(searchSelected === 'queue')
                            ? personTypeQueue = item.code : personTypeSession = item.code"
                          :class="{'is-active': ((searchSelected === 'queue')
                            ? personTypeQueue : personTypeSession) === item.code}"
                          style="min-width: 100px"
                        >
                          {{ item.name }}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="row removeUnderline">
              <div class="field is-horizontal">
                <div class="field-label is-normal">
                  <label class="label">
                    Field to Search
                  </label>
                </div>
                <div class="field-body">
                  <div
                    v-if="searchSelected === 'queue'"
                    class="field control"
                  >
                    <div v-if="!existingQueue || allowQueueEdit">
                      <es-select
                        keyValue="code"
                        ref="resetQueueFields"
                        valueTpl="${description}"
                        @update:values="selectedQueueFields = $event"
                        :allowUserInput="false"
                        :clearOut="true"
                        :errorMsg="msgQueueFields"
                        :hasError="hasErrorQueueFields"
                        :multi="true"
                        :preSelected="preSelectedFields"
                        :search="true"
                        :setValues="fieldOptions"
                      />
                    </div>
                    <div
                      v-else
                      class="tags"
                    >
                      <strong
                        style="font-size: 12px"
                        class="tag source-tag"
                        v-for="(fields, index) in preSelectedFields"
                        :key="index"
                      >
                        {{ fields.description }}
                      </strong>
                    </div>
                  </div>
                  <div v-else class="field-body">
                    <div class="field">
                      <div class="control">
                        <es-select
                          keyValue="code"
                          ref="resetSessionFields"
                          valueTpl="${description}"
                          @update:values="selectedSessionFields = $event"
                          :allowUserInput="false"
                          :clearOut="true"
                          :errorMsg="msgSessionFields"
                          :hasError="hasErrorSessionFields"
                          :multi="true"
                          :preSelected="preSelectedSessionFields"
                          :search="true"
                          :setValues="fieldOptions"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="row removeUnderline">
              <div class="field is-horizontal">
                <div class="field-label is-normal">
                  <label class="label">
                    Search Type
                  </label>
                </div>
                <div class="field-body">
                  <div class="field">
                    <div class="control">
                      SQL (Exact)
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </es-form>
      </div>
    </div>
    <div
      class="is-flex flex-justify--end flex-align--center"
      style="margin-right: 5.15rem; padding-bottom: 2rem"
    >
      <div v-if="searchSelected === 'session'">
        <button
          v-if="!loadingSession"
          class="button button-link has-underline button-text"
          @click="reset"
          style="margin-right: 1rem"
        >
          Reset
        </button>
        <button
          v-if="!loadingSession"
          @click="validateAndExecuteSessionSearch"
          class="button is-primary"
        >
          Search
        </button>
      </div>
      <div v-else>
        <span
          v-if="!loadingQueue
            && !loadingNewQueue
            && (!existingQueue || allowQueueEdit)
            && hasModule('PRSID:IDENT:DUPLICATES:SAVEANDRUN')"
        >
          <button
            class="button button-link has-underline button-text"
            @click="reset"
            style="margin-right: 1rem"
          >
            Reset
          </button>
          <button
            @click="executeQueueSearch"
            class="button is-primary"
          >
            Save and Run Queue
          </button>
        </span>
        <span v-else>
          <span
            v-if="!loadingQueue
              && !loadingNewQueue
              && (existingQueueStatus === 'ERRORED'
                || existingQueueStatus === 'COMPLETED')"
          >
            <span v-if="hasModule('PRSID:IDENT:DUPLICATES:SAVEANDRUN')">
              <button
                @click="editQueue"
                class="button button-link has-underline button-text"
              >
                Edit Queue
              </button>
              <button
                @click="reRunQuery"
                class="button is-primary"
              >
                Re-Run Queue
              </button>
            </span>
          </span>
          <span
            v-if="!loadingQueue
              && !allowQueueEdit
              && !loadingNewQueue
              && (existingQueueStatus === 'QUEUED'
                || existingQueueStatus === 'RUNNING'
                || existingQueueStatus === 'RERUN'
                || existingQueueStatus === 'REFRESH')"
          >
            <!-- <button
              v-if="hasModule('PRSID:IDENT:DUPLICATES:SAVEANDRUN')"
              @click="cancelQueue"
              class="button button-link has-underline button-text"
            >
              Cancel
            </button> -->
            <button
              @click="loadQueue"
              class="button is-primary"
            >
              Refresh
            </button>
          </span>
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import qs from 'qs';
import axios from 'axios';
import { mapActions, mapGetters } from 'vuex';
import EsException from '@/components/EsException.vue';
import EsForm from '@/components/EsForm.vue';
import EsHeader from '@/components/EsHeader.vue';
import EsLoader from '@/components/EsLoader.vue';
import EsSelect from '@/components/EsSelect.vue';
import FieldLabels from '@/mixins/FieldLabels';
import NotificationManager from '@/mixins/NotificationManager';
import PermissionManager from '@/mixins/PermissionManager';

export default {
  mixins: [
    FieldLabels,
    NotificationManager,
    PermissionManager,
  ],
  components: {
    EsException,
    EsForm,
    EsHeader,
    EsLoader,
    EsSelect,
  },
  watch: {
    $route() {
      const { page } = this.$route.query;
      const defaultPage = 1;

      if (this.searchSelected === 'session') {
        this.pageSession = page || defaultPage;

        const savedFields = JSON.parse(localStorage.getItem('SESSION_DUP_QUEUE_FIELDS'));

        // Only load if saved fields exist
        if (savedFields && savedFields.length > 0) {
          this.selectedSessionFields = savedFields;
          this.executeSessionSearch();
        } else {
          this.$emit('sessionResults', { groups: null, personType: null });
          this.loadingErrorSession = false;
          this.$emit('loadingErrorSession', false);
        }
      }

      if (this.searchSelected === 'queue') {
        this.pageQueue = page || defaultPage;
        this.loadQueue();
      }

      this.fieldsByPersonType();
    },
    searchSelected() {
      this.$emit('searchSelected', this.searchSelected);

      const query = { search: this.searchSelected };
      this.$router.push({ name: 'PIDuplicateSearch', query });
    },
    personTypeSession() {
      this.handlePersonTypeChange('sendPersonTypeSession', this.personTypeSession, 'resetSessionFields', 'selectedSessionFields');
    },
    personTypeQueue() {
      this.handlePersonTypeChange('sendPersonTypeQueue', this.personTypeQueue, 'resetQueueFields', 'selectedQueueFields');
    },
  },
  data() {
    return this.initialData();
  },
  async created() {
    await this.loadPersonTypes();
    this.setDataElements();
    if (this.searchSelected === 'session' && this.personTypeSession) {
      this.handlePersonTypeChange('sendPersonTypeSession', this.personTypeSession);

      const savedFields = JSON.parse(localStorage.getItem('SESSION_DUP_QUEUE_FIELDS'));
      if (savedFields && savedFields.length > 0) {
        this.selectedSessionFields = savedFields;
        this.executeSessionSearch();
      } else {
        this.$emit('sessionResults', { groups: null, personType: null });
        this.loadingErrorSession = false;
        this.$emit('loadingErrorSession', false);
      }
    }
    if (this.searchSelected === 'queue') {
      this.loadQueue();
    }
  },
  computed: {
    ...mapGetters('propertiesModule', [
      'properties',
    ]),
    ...mapGetters('lookups', [
      'defaultPersonType',
    ]),
    ...mapGetters('fieldLabelsModule', [
      'getFieldEnabledForPersonType',
    ]),
    contextMode() {
      const identityControl = this.properties['IDENTITY_CONTROL-LIFETIME_IDS'];
      return JSON.parse(identityControl.toString().toLowerCase()) === false;
    },
    preSelectedFields() {
      if (!(this.existingQueue && this.existingQueue.params)) return [];
      const temp = JSON.parse(this.existingQueue.params);
      const fieldCodes = Array.isArray(temp.fields) ? temp.fields : [];
      return this.fieldOptions.filter(f => fieldCodes.includes(f.code));
    },
    preSelectedSessionFields() {
      return this.selectedSessionFields;
    },
    sortedPersonTypesOptions() {
      const sorted = this.userPersonTypes.slice();
      if (!this.contextMode) {
        sorted.unshift({ name: 'All', code: null });
      }
      const compareNames = (a, b) => {
        const nameA = a.name.toUpperCase();
        const nameB = b.name.toUpperCase();
        if (nameA < nameB) return -1;
        if (nameA > nameB) return 1;
        return 0;
      };
      return sorted.sort(compareNames);
    },
    searchParams() {
      return this.paramsSerializer(this.urlParams);
    },
    urlParams() {
      const params = {
        search: this.searchSelected,
        page: this.$route.query.page || 1,
      };

      if (this.searchSelected === 'session') {
        Object.assign(params, {
          requestType: 'ON_DEMAND',
          personType: this.personTypeSession || undefined,
          fields: this.selectedSessionFields.length
            ? this.selectedSessionFields.map(s => s.code) : (JSON.parse(localStorage.getItem('SESSION_DUP_QUEUE_FIELDS')) || []).map(a => a.code),
          sort: '[{"field":"count","order":"DESC"}]',
        });
      } else if (this.searchSelected === 'queue') {
        const queueName = this.existingQueue ? this.existingQueueName : this.queueName;
        Object.assign(params, {
          requestType: 'QUEUE',
          personType: this.personTypeQueue || undefined,
          fields: this.selectedQueueFields.length
            ? this.selectedQueueFields.map(s => s.code) : this.preSelectedFields.map(s => s.code),
          page: this.pageQueue,
          queryId: this.existingQueueId || undefined,
          name: queueName || undefined,
        });
      }

      return params;
    },
  },
  methods: {
    ...mapActions('lookups', [
      'loadPersonTypes',
    ]),
    async cancelQueue() {
      try {
        this.loadingQueue = true;
        const response = await axios({
          method: 'put',
          url: `idmanager/duplicates/${this.existingQueue.id}`,
          data: {
            status: 'ERRORED',
          },
        });
        if (response.status === 200) {
          this.existingQueue = response.data;
          this.existingQueueName = this.existingQueue.name;
          this.existingQueueStatus = this.existingQueue.status;
        }
      } catch (e) {
        this.showNotification('danger', [e.response.data.message]);
      } finally {
        this.loadingQueue = false;
        this.loadQueue();
      }
    },
    editQueue() {
      this.allowQueueEdit = !this.allowQueueEdit;
    },
    async executeQueueSearch() {
      this.validateField();
      if (this.hasErrorQueueFields || this.hasErrorQueueName) {
        return;
      }
      try {
        this.loadingNewQueue = true;
        this.loadingErrorNewQueue = false;
        this.$emit('loadingQueue', true);
        this.$emit('loadingErrorQueue', false);
        const response = await this.newDupQueue();
        if (response.status === 200) {
          this.existingQueue = response.data;
          this.existingQueueId = this.existingQueue.id;
          this.existingQueueName = this.existingQueue.name;
          this.existingQueueStatus = this.existingQueue.status;
          const groups = await this.loadQueueGroups();
          this.$emit('dupQueueResults', groups.data);
        }
        this.allowQueueEdit = false;
      } catch (e) {
        this.loadingErrorNewQueue = true;
        this.$emit('loadingErrorQueue', true);
      } finally {
        this.loadingNewQueue = false;
        this.$emit('loadingQueue', false);
      }
    },
    async executeSessionSearch() {
      try {
        this.loadingSession = true;
        this.loadingErrorSession = false;
        this.$emit('loadingSession', true);
        this.$emit('loadingErrorSession', false);
        localStorage.setItem('SESSION_DUP_QUEUE_FIELDS', JSON.stringify(this.selectedSessionFields));
        localStorage.setItem('SESSION_DUP_QUEUE_PERSONTYPE', JSON.stringify(this.personTypeSession));
        const response = await this.newDupQueue();
        if (response.status === 200) {
          localStorage.setItem('SESSION_DUP_QUEUE_FIELDS', JSON.stringify(this.selectedSessionFields));
          localStorage.setItem('SESSION_DUP_QUEUE_PERSONTYPE', JSON.stringify(this.personTypeSession));
          this.$emit('sessionResults', { groups: response.data.groups, personType: this.personTypeSession });
        }
      } catch (e) {
        this.loadingErrorSession = true;
        this.$emit('loadingErrorSession', true);
      } finally {
        this.loadingSession = false;
        this.$emit('loadingSession', false);
      }
    },
    fieldEnabled(code, personType) {
      return this.getFieldEnabledForPersonType(code, 'ASSIGN', personType);
    },
    fieldsByPersonType() {
      let temp;

      if (this.searchSelected === 'session' && this.personTypeSession !== null) {
        temp = this.personTypeSession;
      } else if (this.searchSelected === 'queue' && this.personTypeQueue !== null) {
        temp = this.personTypeQueue;
      } else {
        temp = this.userPersonTypesCodes.includes(this.defaultPersonType)
          ? this.defaultPersonType
          : this.userPersonTypesCodes[0];
      }

      const fieldCodes = [
        {
          code: 'FIRST_NAME',
          enabled: this.fieldEnabled('FIRST_NAME', temp),
        },
        {
          code: 'LAST_NAME',
          enabled: this.fieldEnabled('LAST_NAME', temp),
        },
        {
          code: 'MIDDLE_NAME',
          enabled: this.fieldEnabled('MIDDLE_NAME', temp),
        },
        {
          code: 'GENDER_CODE',
          enabled: this.fieldEnabled('GENDER_CODE', temp),
        },
        {
          code: 'DATE_OF_BIRTH',
          enabled: this.fieldEnabled('DATE_OF_BIRTH', temp),
        },
        {
          code: 'SOCIAL_SECURITY_NUMBER',
          enabled: this.fieldEnabled('SOCIAL_SECURITY_NUMBER', temp),
        },
      ];

      this.fieldOptions = fieldCodes
        .filter(field => field.enabled) // keep only enabled fields
        .map(field => ({
          code: field.code,
          description: this.getFieldLabel(`PER:${temp}:ASSIGN:${field.code}`),
        }));
    },
    handlePersonTypeChange(emitEvent, personType, refName, selectedFieldName) {
      this.$emit(emitEvent, personType);
      if (this[selectedFieldName] && this[selectedFieldName].length !== 0) {
        this[selectedFieldName] = [];
      }
      if (this.$refs[refName] && this.$refs[refName].clear) {
        this.$refs[refName].clear();
      }
      this.fieldsByPersonType();
    },
    initialData() {
      return {
        allowQueueEdit: false,
        existingQueue: null,
        existingQueueId: null,
        existingQueueName: null,
        existingQueueStatus: null,
        fieldOptions: [],
        formErrorMsg: 'There were some errors. Please fix the errors highlighted in the fields below.',
        hasErrorSessionFields: false,
        hasErrorQueueFields: false,
        hasErrorQueueName: false,
        loadingErrorSession: false,
        loadingErrorQueue: false,
        loadingNewQueue: false,
        loadingErrorNewQueue: false,
        loadingSession: false,
        loadingQueue: false,
        msgSessionFields: null,
        msgQueueFields: null,
        msgQueueName: null,
        pageQueue: this.$route.query.page ? this.$route.query.page : 1,
        pageSession: this.$route.query.page ? this.$route.query.page : 1,
        personTypeQueue: null,
        personTypeSession: JSON.parse(localStorage.getItem('SESSION_DUP_QUEUE_PERSONTYPE'))
          ? JSON.parse(localStorage.getItem('SESSION_DUP_QUEUE_PERSONTYPE')) : null,
        queueName: null,
        searchSelected: this.$route.query.search,
        searchTypes: [],
        selectedSessionFields: JSON.parse(localStorage.getItem('SESSION_DUP_QUEUE_FIELDS'))
          ? JSON.parse(localStorage.getItem('SESSION_DUP_QUEUE_FIELDS')) : [],
        selectedQueueFields: [],
        userPersonTypes: this.$store.state.user.userPersonTypes
          .map(item => item.personType).filter(a => a.status === true),
        userPersonTypesCodes: null,
      };
    },
    loadExistingQueue() {
      return axios.get('idmanager/duplicates?requestType=QUEUE');
    },
    loadQueueGroups() {
      return axios.get(`idmanager/duplicates/${this.existingQueueId}/groups?page=${this.pageQueue}&sort=%5B%7B%22field%22%3A%22count%22%2C%22order%22%3A%22DESC%22%7D%5D`);
    },
    newDupQueue() {
      this.$router.replace({ query: this.urlParams });
      return axios.post(`idmanager/duplicates?${this.searchParams}`);
    },
    async loadQueue() {
      try {
        this.loadingQueue = true;
        this.loadingErrorQueue = false;
        this.$emit('loadingQueue', true);
        this.$emit('loadingErrorQueue', false);
        const queue = await this.loadExistingQueue();
        if (queue.status === 200) {
          this.existingQueue = queue.data;
          this.existingQueueId = this.existingQueue.id;
          this.existingQueueName = this.existingQueue.name;
          this.existingQueueStatus = this.existingQueue.status;
          const groups = await this.loadQueueGroups();
          this.$emit('dupFields', this.existingQueue.params);
          this.$emit('dupQueueResults', groups.data);
          this.personTypeQueue = JSON.parse(this.existingQueue.params).personType;
        }
      } catch (e) {
        this.loadingErrorQueue = true;
        this.$emit('loadingErrorQueue', true);
      } finally {
        this.loadingQueue = false;
        this.$emit('loadingQueue', false);
      }
    },
    paramsSerializer(params) {
      return qs.stringify(params, { arrayFormat: 'repeat' });
    },
    async reRunQuery() {
      try {
        this.loadingQueue = true;
        const response = await axios({
          method: 'put',
          url: `idmanager/duplicates/${this.existingQueue.id}`,
          data: {
            status: 'RERUN',
          },
        });
        if (response.status === 200) {
          this.existingQueue = response.data;
          this.existingQueueId = this.existingQueue.id;
          this.existingQueueName = this.existingQueue.name;
          this.existingQueueStatus = this.existingQueue.status;
        }
      } catch (e) {
        this.showNotification('danger', [e.response.data.message]);
      } finally {
        this.loadingQueue = false;
        this.loadQueue();
      }
    },
    reset() {
      localStorage.removeItem('SESSION_DUP_QUEUE_FIELDS');
      localStorage.removeItem('SESSION_DUP_QUEUE_PERSONTYPE');
      if (this.searchSelected === 'queue') {
        this.queueName = null;
        this.hasErrorQueueName = false;
        this.msgQueueName = null;
        this.hasErrorQueueFields = false;
        this.msgQueueFields = null;
        this.personTypeQueue = this.userPersonTypesCodes.includes(this.defaultPersonType)
          ? this.defaultPersonType : this.userPersonTypesCodes[0];
        if (this.selectedQueueFields.length !== 0) {
          this.selectedQueueFields = [];
          this.$refs.resetQueueFields.clear();
        }
        this.$emit('dupQueueResults', null);
      }
      if (this.searchSelected === 'session') {
        this.hasErrorSessionFields = false;
        this.msgSessionFields = null;
        this.personTypeSession = this.userPersonTypesCodes.includes(this.defaultPersonType)
          ? this.defaultPersonType : this.userPersonTypesCodes[0];
        if (this.selectedSessionFields.length !== 0) {
          this.selectedSessionFields = [];
          this.$refs.resetSessionFields.clear();
        }
        this.$emit('sessionResults', { groups: null, personType: null });
      }
      this.hideNotification();
    },
    resetNotifications() {
      this.$store.state.notifications.isActive = false;
      this.$store.state.notifications.notificationType = null;
    },
    setDataElements() {
      this.selectedSessionFields = JSON.parse(localStorage.getItem('SESSION_DUP_QUEUE_FIELDS'))
        ? JSON.parse(localStorage.getItem('SESSION_DUP_QUEUE_FIELDS')) : [];
      this.userPersonTypesCodes = this.userPersonTypes
        .map(item => item.code);
      this.fieldsByPersonType();
      this.searchTypes = [
        {
          code: 'session',
          description: 'Session',
        },
        {
          code: 'queue',
          description: 'Queue',
        },
      ];
      const key = 'SESSION_DUP_QUEUE_PERSONTYPE';
      const exists = Object.prototype.hasOwnProperty.call(localStorage, key);

      if (exists) {
        const raw = localStorage.getItem(key);
        try {
          this.personTypeSession = raw ? JSON.parse(raw) : null;
        } catch {
          this.personTypeSession = null;
        }
      } else if (this.userPersonTypesCodes.includes(this.defaultPersonType)) {
        this.personTypeSession = this.defaultPersonType;
      } else {
        [this.personTypeSession] = this.userPersonTypesCodes;
      }
    },
    showError(error) {
      this.showNotification('danger', [error]);
    },
    validateAndExecuteSessionSearch() {
      this.validateField();
      if (!this.hasErrorSessionFields) {
        this.executeSessionSearch();
      }
    },
    validateField() {
      if (!this.selectedSessionFields.length) {
        this.hasErrorSessionFields = true;
        this.msgSessionFields = 'Field to Search is required.';
        this.showError(this.formErrorMsg);
      } else {
        this.hasErrorSessionFields = false;
        this.msgSessionFields = null;
        this.resetNotifications();
      }
      if (this.searchSelected === 'queue') {
        const queueName = this.existingQueue ? this.existingQueueName : this.queueName;
        if (!queueName) {
          this.hasErrorQueueName = true;
          this.msgQueueName = 'Queue Name is required.';
        } else {
          this.hasErrorQueueName = false;
          this.msgQueueName = null;
          this.resetNotifications();
        }
        if ((this.selectedQueueFields && this.selectedQueueFields.length)
          || (this.preSelectedFields && this.preSelectedFields.length)) {
          this.hasErrorQueueFields = false;
          this.msgQueueFields = null;
          this.resetNotifications();
        } else {
          this.hasErrorQueueFields = true;
          this.msgQueueFields = 'Field to Search is required.';
          this.showError(this.formErrorMsg);
        }
      }
    },
    validateQueueName() {
      this.hasErrorQueueName = this.queueName.length > 60;
      this.msgQueueName = this.hasErrorQueueName ? 'Queue Name allows a maximum of 60 characters.' : null;
    },
  },
};
</script>

<style lang="sass" scoped>
.button-text
  font-size: 12px
.is-static
  width: fit-content
  padding-left: 0
  border: none !important
  background: transparent !important
  box-shadow: none !important
  cursor: default !important
  color: #363636 !important
  pointer-events: none
.queue-info
  padding-left: 0
  font-size: 12px
  display: flex
  align-items: center
</style>
